import { useMemo } from 'react';
import { Target, Clock } from 'lucide-react';
import { Card, StatusPill, formatDatum, formatEuro } from '@elbklar/ui';
import type { SalesGoalData } from '@/lib/api';

interface GoalTrackerProps {
  goal: SalesGoalData;
}

const formatCurrency = (value: number) => formatEuro(value);

export function GoalTracker({ goal }: GoalTrackerProps) {
  const { closed_won_value, target_value, target_date, weighted_pipeline_value } = goal;

  const progress = useMemo(() => {
    return Math.min((closed_won_value / target_value) * 100, 100);
  }, [closed_won_value, target_value]);

  const daysRemaining = useMemo(() => {
    const today = new Date();
    const deadline = new Date(target_date);
    const diff = deadline.getTime() - today.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [target_date]);

  const totalDays = useMemo(() => {
    // Assume goal started ~3 months before deadline
    const deadline = new Date(target_date);
    const start = new Date(deadline);
    start.setMonth(start.getMonth() - 3);
    const today = new Date();
    const elapsed = Math.max(1, Math.ceil((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
    const total = Math.ceil((deadline.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return { elapsed, total };
  }, [target_date]);

  const isOnTrack = useMemo(() => {
    const timeProgress = ((totalDays.total - daysRemaining) / totalDays.total) * 100;
    return progress >= timeProgress * 0.85; // 15% tolerance
  }, [progress, daysRemaining, totalDays]);

  const remaining = target_value - closed_won_value;
  const projectedWithPipeline = closed_won_value + weighted_pipeline_value;
  const pipelineCoversGap = projectedWithPipeline >= target_value;

  return (
    <Card
      icon={Target}
      title="Umsatzziel Q2"
      description={`Bis ${formatDatum(target_date)}`}
      actions={
        progress >= 100 ? (
          <StatusPill tone="success">Ziel erreicht</StatusPill>
        ) : isOnTrack ? (
          <StatusPill tone="highlight">Im Plan</StatusPill>
        ) : (
          <StatusPill tone="warning">Aufholen</StatusPill>
        )
      }
    >
      <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[1fr_auto]">
        {/* Fortschritt */}
        <div className="space-y-3">
          <div className="flex items-baseline gap-2">
            <span className="t-stat tabular">{formatCurrency(closed_won_value)}</span>
            <span className="t-title text-muted-foreground">/ {formatCurrency(target_value)}</span>
            <span className="ml-auto t-mono text-muted-foreground">{Math.round(progress)} %</span>
          </div>

          <div
            className="relative h-2 overflow-hidden rounded-full bg-accent"
            role="meter"
            aria-label="Fortschritt zum Umsatzziel"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
          >
            {/* Gewichtete Pipeline als Vorschau, heller */}
            {projectedWithPipeline > closed_won_value && (
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-selection/35 transition-[width] duration-500"
                style={{ width: `${Math.min((projectedWithPipeline / target_value) * 100, 100)}%` }}
              />
            )}
            {/* Closed Won */}
            <div
              className={`absolute inset-y-0 left-0 rounded-full transition-[width] duration-500 ${progress >= 100 ? 'bg-success' : 'bg-selection'}`}
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 t-small text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-selection" />
              Gewonnen
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-selection/35" />
              + Gewichtete Pipeline ({formatCurrency(weighted_pipeline_value)})
            </span>
          </div>
        </div>

        {/* Kennzahlen */}
        <div className="flex gap-6 border-border lg:flex-col lg:gap-3 lg:border-l lg:pl-6">
          <div className="lg:text-right">
            <div className="flex items-center gap-1.5 lg:justify-end">
              <Clock size={14} strokeWidth={1.75} className="text-muted-foreground" aria-hidden />
              <span className="t-stat tabular">{daysRemaining}</span>
            </div>
            <span className="t-label text-muted-foreground">Tage verbleibend</span>
          </div>
          <div className="lg:text-right">
            <div className="t-stat tabular">{formatCurrency(Math.max(0, remaining))}</div>
            <span className="t-label text-muted-foreground">noch offen</span>
          </div>
          {pipelineCoversGap && remaining > 0 && (
            <div className="lg:text-right">
              <span className="t-label text-success">Pipeline deckt Restbetrag</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
